// @erp/module-cms/schemas — site-wide CMS settings wire contracts. Browser-safe:
// zod + plain data only (fe forms and the gateway parse the SAME schemas, so the
// admin UI and the server can never disagree on what a valid setting is).
//
// Scope (2026-08-19 product decision): the CMS is the DYNAMIC-CONTENT backend for
// a STATIC site, locale-ready but English-first. Settings are a KEY/VALUE store
// (the MBSPSU settings(key,value) shape): every value is a TEXT scalar, so the
// wire is a flat `Record<key,string>` — no per-value (de)serialization, the store
// speaks strings end to end. The typed GROUPS below organize the keys (site
// identity / publishing / slug / ref-no / contact / social / newsletter) and pin
// each key's validity (enums, bounded text, integer-strings); the DEFAULT_SETTINGS
// map gives every key a sensible starter value so a never-written store still
// yields a complete, publishable configuration.
//
// The upsert body is a STRICT PARTIAL over the known keys: partial ⇒ a PATCH
// touches only the keys it sends; strict ⇒ an unknown key is a VALIDATION 400
// (zod), never a silently-dropped field and never a placeholder error code.
import { z } from "zod";
import { CmsPublicLocales, LocalesSettingValue } from "./locale.js";
import { READING_SETTINGS_DEFAULT, ReadingSettingValue, serializeReadingSettings } from "./reading.js";

// ── Typed vocabularies (a fixed value set ⇒ an enum, so a bad value 400s) ──────

export const PUBLISHING_STATUSES = ["draft", "published", "archived"] as const;
export const PublishingStatus = z.enum(PUBLISHING_STATUSES);
export type PublishingStatus = z.infer<typeof PublishingStatus>;

export const NEW_TAG_UNITS = ["days", "weeks", "months"] as const;
export const NewTagUnit = z.enum(NEW_TAG_UNITS);
export type NewTagUnit = z.infer<typeof NewTagUnit>;

export const SLUG_FORMATS = ["title", "title-uuid", "date-title", "ref-title", "uuid-only"] as const;
export const SlugFormat = z.enum(SLUG_FORMATS);
export type SlugFormat = z.infer<typeof SlugFormat>;

export const REF_NO_SEQ_TYPES = ["numeric", "alphabetic", "alphanumeric"] as const;
export const RefNoSeqType = z.enum(REF_NO_SEQ_TYPES);
export type RefNoSeqType = z.infer<typeof RefNoSeqType>;

export const NEWSLETTER_PROVIDERS = ["local", "mailchimp", "mailerlite"] as const;
export const NewsletterProvider = z.enum(NEWSLETTER_PROVIDERS);
export type NewsletterProvider = z.infer<typeof NewsletterProvider>;

/** A non-negative integer carried as a string (the store is TEXT — parse at the edge that needs a number). */
const intStr = z.string().regex(/^\d+$/, "must be a non-negative integer");
/** Bounded free text — the max keeps a runaway value out of the store. */
const text = (max: number) => z.string().max(max);

// ── The known-key surface: one validator per key, grouped by concern ───────────
// The single source of truth for WHICH keys exist and WHAT each accepts. Both the
// key list and the upsert schema derive from it, so the two can never drift.
const SETTING_FIELDS = {
  // Site identity — the static site's brand front door.
  site_name: text(200),
  site_tagline: text(300),
  site_logo: text(1000),
  site_logo_dark: text(1000),
  site_favicon: text(1000),
  // Publishing defaults — how new content is born and listed.
  default_publishing_status: PublishingStatus,
  list_page_limit: intStr,
  trash_retention_days: intStr,
  new_tag_duration_val: intStr,
  new_tag_duration_unit: NewTagUnit,
  // Slug format — the URL grammar for generated content.
  slug_format: SlugFormat,
  // Ref-no sequence — placeholder config for the reference-number engine.
  ref_no_prefix: text(100),
  ref_no_postfix: text(100),
  ref_no_seq_type: RefNoSeqType,
  ref_no_seq_length: intStr,
  ref_no_seq_current: text(24),
  // Tenders — auto-post a companion announcement when a tender is published (the
  // create/update path defers to the per-request createAnnouncement flag first; this
  // setting is the fallback default). A boolean carried as an enum'd string.
  tender_auto_post_announcement: z.enum(["true", "false"]),
  // Contact — the public-facing coordinates.
  contact_email: text(200),
  contact_phone: text(50),
  contact_address: text(500),
  // Social — profile links surfaced on the site.
  social_facebook: text(1000),
  social_instagram: text(1000),
  social_twitter: text(1000),
  social_youtube: text(1000),
  social_linkedin: text(1000),
  // Newsletter — the ESP wiring. newsletter_api_key is a SECRET (see PUBLIC_SETTING_KEYS).
  newsletter_provider: NewsletterProvider,
  newsletter_api_key: text(500),
  newsletter_list_id: text(200),
  // Locales — the ENABLED list (the locale contract, item 3; seeded by db-cms 0020): a
  // JSON array of canonical tags, unique, non-empty, re-serialised canonical on the way
  // in. That it contains DEFAULT_LOCALE and that a dropped locale has no rows are the
  // SERVICE's checks (writeSettings — the schema knows no env and no rows). Not in a
  // CMS_SETTING_GROUPS group: the Settings screen renders it through its own editor, and
  // the public read exposes it STRUCTURED (defaultLocale / locales / localeNames), never
  // as this raw string.
  locales: LocalesSettingValue,
  // Reading — THE READING GATE (ADR-010 §2.12; seeded by db-cms 0024): one JSON object
  // { defaultAccess, openAfterMonths, allowDownload } (schemas/reading.ts ReadingSettings),
  // re-serialised canonical on the way in. Not in a CMS_SETTING_GROUPS group: the Settings
  // screen renders it through its own "Reading access" section, and it never reaches the
  // public read — the site learns the EFFECTIVE answer per publication (`readAccess`).
  reading: ReadingSettingValue,
} satisfies Record<string, z.ZodType>;

/** The canonical key union — every setting the CMS knows about. */
export type CmsSettingKey = keyof typeof SETTING_FIELDS;

/** The known-key list (runtime) — the service validates a patch's keys against this set. */
export const CMS_SETTING_KEYS = Object.keys(SETTING_FIELDS) as CmsSettingKey[];

/**
 * The starter configuration — every key with a sensible, English-first default so
 * a never-written store still publishes. Stored values override these on read
 * (repo.getAll merges stored over DEFAULT_SETTINGS); a missing row never breaks
 * publishing defaults / ref-no sequences / slug format / site identity.
 */
export const DEFAULT_SETTINGS: Record<CmsSettingKey, string> = {
  site_name: "Shippra",
  site_tagline: "A modern operations platform",
  site_logo: "",
  site_logo_dark: "",
  site_favicon: "",
  default_publishing_status: "draft",
  list_page_limit: "10",
  trash_retention_days: "30",
  new_tag_duration_val: "7",
  new_tag_duration_unit: "days",
  slug_format: "title",
  ref_no_prefix: "",
  ref_no_postfix: "",
  ref_no_seq_type: "numeric",
  ref_no_seq_length: "6",
  ref_no_seq_current: "1",
  tender_auto_post_announcement: "true",
  contact_email: "",
  contact_phone: "",
  contact_address: "",
  social_facebook: "",
  social_instagram: "",
  social_twitter: "",
  social_youtube: "",
  social_linkedin: "",
  newsletter_provider: "local",
  newsletter_api_key: "",
  newsletter_list_id: "",
  // The DDL-authored default ('en', db-cms 0002..0008) — what a never-seeded store reads
  // as; the read rule (service readEnabledLocales) unions the env's DEFAULT_LOCALE in.
  locales: '["en"]',
  // The product default (the owner, 2026-09-20): members-only, no wall, no downloads — what
  // 0024 seeds and what a never-written store reads as (service readReadingSettings).
  reading: serializeReadingSettings(READING_SETTINGS_DEFAULT),
};

/**
 * The typed GROUPS — the admin UI's section layout and the documentation of what
 * lives where. `satisfies` proves every listed key is a real CmsSettingKey, so a
 * renamed/removed key breaks the build here rather than silently orphaning a group.
 */
export const CMS_SETTING_GROUPS = {
  site_identity: ["site_name", "site_tagline", "site_logo", "site_logo_dark", "site_favicon"],
  publishing: [
    "default_publishing_status",
    "list_page_limit",
    "trash_retention_days",
    "new_tag_duration_val",
    "new_tag_duration_unit",
  ],
  slug: ["slug_format"],
  ref_no: ["ref_no_prefix", "ref_no_postfix", "ref_no_seq_type", "ref_no_seq_length", "ref_no_seq_current"],
  tenders: ["tender_auto_post_announcement"],
  contact: ["contact_email", "contact_phone", "contact_address"],
  social: ["social_facebook", "social_instagram", "social_twitter", "social_youtube", "social_linkedin"],
  newsletter: ["newsletter_provider", "newsletter_api_key", "newsletter_list_id"],
} as const satisfies Record<string, readonly CmsSettingKey[]>;

/**
 * The PUBLIC allowlist (DEFAULT-DENY) — the ONLY keys the unauthenticated
 * /settings/public read exposes. Site identity + contact + social + the new-tag
 * durations the static site needs to render; everything admin-internal (publishing
 * defaults, slug/ref-no engine config, trash retention) and everything SECRET
 * (the whole newsletter group — provider, list id, and above all newsletter_api_key)
 * is excluded by omission. A new key is private until it is added here by hand.
 */
export const PUBLIC_SETTING_KEYS = [
  "site_name",
  "site_tagline",
  "site_logo",
  "site_logo_dark",
  "site_favicon",
  "contact_email",
  "contact_phone",
  "contact_address",
  "social_facebook",
  "social_instagram",
  "social_twitter",
  "social_youtube",
  "social_linkedin",
  "new_tag_duration_val",
  "new_tag_duration_unit",
] as const satisfies readonly CmsSettingKey[];

/**
 * GET /settings/public's wire shape (the site mirrors it): the allowlisted keys, every one a
 * string (a default fills a never-written key), PLUS the locale block — `defaultLocale`,
 * `locales` and `localeNames` (the locale contract, item 5). The raw `locales` setting
 * string is NOT among the keys: the site reads the structured block.
 */
export const CmsPublicSettings = CmsPublicLocales.extend(
  Object.fromEntries(PUBLIC_SETTING_KEYS.map((k) => [k, z.string()])) as Record<(typeof PUBLIC_SETTING_KEYS)[number], z.ZodString>,
);
export type CmsPublicSettings = z.infer<typeof CmsPublicSettings>;

/**
 * SECRETS — keys NEVER returned to the browser with their stored value (write-through
 * credentials). The admin read BLANKS these: the editor sets a NEW value write-through
 * and never needs to SEE the stored one, so the secret stays off the wire, out of the
 * client cache, and out of the DOM. Distinct from PUBLIC_SETTING_KEYS (the anonymous
 * subset); server-side consumers read the raw store directly, not through the admin read.
 */
export const SECRET_SETTING_KEYS = ["newsletter_api_key"] as const satisfies readonly CmsSettingKey[];

/**
 * The upsert body — a STRICT PARTIAL over the known keys. `.partial()` gives PATCH
 * semantics (only the sent keys are written); the strict object rejects an unknown
 * key as a zod VALIDATION 400. Values are strings (the store is TEXT), each held to
 * its group's validator.
 */
export const UpsertCmsSettings = z.strictObject(SETTING_FIELDS).partial();
export type UpsertCmsSettings = z.infer<typeof UpsertCmsSettings>;
